import { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Search, Wrench, Filter, History, UserPlus } from 'lucide-react';

import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { SemaforoBadge } from '@/components/mantenimiento/SemaforoBadge';
import { EjecutarMantenimientoDialog } from '@/components/mantenimiento/EjecutarMantenimientoDialog';
import { HistorialEquipoDialog } from '@/components/inventario/HistorialEquipoDialog';
import { AsignarEquipoDialog } from '@/components/inventario/AsignarEquipoDialog';

import { getMantenimientos } from '@/services/api/mantenimiento.api';
import type { MantenimientoItem, SemaforoMto, EstadoItem } from '@/types/dominio';
import { cn, formatNumber } from '@/lib/utils';
import { ReportButton } from '@/components/reportes/ReportButton';

type Filter = 'TODOS' | SemaforoMto;

// Mapping de colores por estado del activo. Espejo del enum EstadoItem del backend.
// Decidimos colores neutros (no semánticos como rojo/verde) para que NO compitan
// visualmente con el semáforo de mantenimiento, que sí es la señal principal.
const ESTADO_META: Record<EstadoItem, { label: string; classes: string }> = {
  EN_BODEGA:     { label: 'En bodega',    classes: 'bg-emerald-50 text-emerald-700 ring-emerald-200' },
  ASIGNADO:      { label: 'Asignado',     classes: 'bg-primary-50 text-primary-700 ring-primary-200' },
  EN_REPARACION: { label: 'Reparación',   classes: 'bg-amber-50 text-amber-800 ring-amber-200' },
  DADO_DE_BAJA:  { label: 'De baja',      classes: 'bg-slate-100 text-slate-600 ring-slate-200' },
};

function EstadoBadge({ estado }: { estado: EstadoItem }) {
  const meta = ESTADO_META[estado];
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1',
        meta.classes,
      )}
    >
      {meta.label}
    </span>
  );
}

const FILTERS: Array<{ value: Filter; label: string; tone: string }> = [
  { value: 'TODOS',  label: 'Todos',          tone: 'bg-slate-100 text-slate-700 hover:bg-slate-200' },
  { value: 'DANGER', label: 'Vencidos',       tone: 'bg-red-100 text-red-700 hover:bg-red-200' },
  { value: 'WARN',   label: 'Próximos',       tone: 'bg-amber-100 text-amber-800 hover:bg-amber-200' },
  { value: 'OK',     label: 'Al día',         tone: 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' },
  { value: 'SIN_PROGRAMAR', label: 'Sin programar', tone: 'bg-slate-100 text-slate-700 hover:bg-slate-200' },
];

export function MantenimientoPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filterParam = (searchParams.get('semaforo') as Filter | null) ?? 'TODOS';
  const [busqueda, setBusqueda] = useState('');
  const [selected, setSelected] = useState<MantenimientoItem | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Estado separado para el dialog de historial — usa el id del item, no el item completo,
  // porque la respuesta del backend incluye toda la cabecera del activo en su payload.
  const [historialItemId, setHistorialItemId] = useState<string | null>(null);
  const [historialOpen, setHistorialOpen] = useState(false);

  // Dialog de asignación (HU-4.3) — sólo se abre para items EN_BODEGA.
  const [asignarItem, setAsignarItem] = useState<MantenimientoItem | null>(null);
  const [asignarOpen, setAsignarOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['mantenimientos', filterParam, busqueda],
    queryFn: () => getMantenimientos({
      semaforo: filterParam === 'TODOS' ? undefined : filterParam,
      busqueda: busqueda || undefined,
      pageSize: 100,
    }),
  });

  // Conteos derivados para mostrar en los filtros
  const conteos = useMemo(() => {
    const base = { TODOS: data?.total ?? 0, OK: 0, WARN: 0, DANGER: 0, SIN_PROGRAMAR: 0 };
    if (data?.items && filterParam === 'TODOS') {
      data.items.forEach((it) => { base[it.semaforo] += 1; });
    }
    return base;
  }, [data, filterParam]);

  function setFilter(v: Filter) {
    if (v === 'TODOS') setSearchParams({});
    else setSearchParams({ semaforo: v });
  }

  function openEjecutar(item: MantenimientoItem) {
    setSelected(item);
    setDialogOpen(true);
  }

  function openHistorial(item: MantenimientoItem) {
    setHistorialItemId(item.id_item_maestro);
    setHistorialOpen(true);
  }

  function openAsignar(item: MantenimientoItem) {
    setAsignarItem(item);
    setAsignarOpen(true);
  }

  return (
    <div className="space-y-6">
      {/* ── Filtros (chips) ─────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-2">
        <Filter className="h-4 w-4 text-muted-foreground" />
        {FILTERS.map((f) => {
          const active = filterParam === f.value;
          const count = conteos[f.value];
          return (
            <button
              key={f.value}
              onClick={() => setFilter(f.value)}
              className={cn(
                'inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium transition-all border',
                active
                  ? 'bg-primary-700 text-white border-primary-700 shadow-soft'
                  : `${f.tone} border-transparent`,
              )}
            >
              <span>{f.label}</span>
              {filterParam === 'TODOS' && f.value !== 'TODOS' && (
                <span className={cn('tabular text-[10px] rounded-full px-1.5 py-0.5 bg-white/40', active && 'text-white')}>
                  {formatNumber(count)}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* ── Buscador ────────────────────────────────────────────────────── */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por serial, nombre o colaborador..."
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                className="pl-9"
              />
            </div>
            <ReportButton
              reporte="mantenimiento-historial"
              filtros={{ serial: busqueda || undefined }}
            />
          </div>
        </CardContent>
      </Card>

      {/* ── Tabla ───────────────────────────────────────────────────────── */}
      <Card className="overflow-hidden p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[160px]">Serial</TableHead>
              <TableHead>Activo</TableHead>
              <TableHead className="hidden md:table-cell">Colaborador</TableHead>
              <TableHead className="hidden sm:table-cell">Estado</TableHead>
              <TableHead className="hidden lg:table-cell">Próximo mto.</TableHead>
              <TableHead>Semáforo</TableHead>
              <TableHead className="text-right w-[200px]">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              [...Array(8)].map((_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={7}><Skeleton className="h-10 w-full" /></TableCell>
                </TableRow>
              ))
            ) : !data?.items.length ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-12">
                  <div className="flex flex-col items-center gap-2 text-muted-foreground">
                    <Wrench className="h-8 w-8 opacity-40" />
                    <p className="text-sm">No hay activos que coincidan con los filtros</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              data.items.map((it) => (
                <TableRow key={it.id_item_maestro}>
                  <TableCell className="font-mono text-xs font-medium">{it.serial_global}</TableCell>
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="font-medium text-sm">{it.nombre_glpi ?? '—'}</span>
                      <span className="text-xs text-muted-foreground capitalize">
                        {[it.tipo?.toLowerCase(), it.marca?.toLowerCase()].filter(Boolean).join(' · ')}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    {it.empleado_asignado ? (
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">{it.empleado_asignado.nombres}</span>
                        <span className="text-xs text-muted-foreground tabular">{it.empleado_asignado.documento}</span>
                      </div>
                    ) : (
                      <Badge variant="outline" className="font-normal">Sin asignar</Badge>
                    )}
                  </TableCell>
                  <TableCell className="hidden sm:table-cell">
                    <EstadoBadge estado={it.estado_actual} />
                  </TableCell>
                  <TableCell className="hidden lg:table-cell tabular text-sm">
                    {it.fecha_proximo_mto
                      ? new Date(it.fecha_proximo_mto).toLocaleDateString('es-CO')
                      : '—'}
                  </TableCell>
                  <TableCell>
                    <SemaforoBadge value={it.semaforo} dias={it.dias_para_proximo} />
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openHistorial(it)}
                        aria-label="Ver historial"
                        title="Ver historial"
                      >
                        <History className="h-3.5 w-3.5" />
                        <span className="hidden xl:inline">Historial</span>
                      </Button>
                      {/* "Asignar" sólo aplica cuando el activo está disponible en bodega */}
                      {it.estado_actual === 'EN_BODEGA' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openAsignar(it)}
                          title="Asignar a empleado"
                          className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"
                        >
                          <UserPlus className="h-3.5 w-3.5" />
                          <span className="hidden xl:inline">Asignar</span>
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => openEjecutar(it)}>
                        <Wrench className="h-3.5 w-3.5" />
                        Ejecutar
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* ── Modal ejecutar ──────────────────────────────────────────────── */}
      <EjecutarMantenimientoDialog
        item={selected}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      />

      {/* ── Modal historial (HU-4.4) ────────────────────────────────────── */}
      <HistorialEquipoDialog
        idItemMaestro={historialItemId}
        open={historialOpen}
        onOpenChange={setHistorialOpen}
      />

      {/* ── Modal asignar equipo + fotos (HU-4.3) ──────────────────────── */}
      <AsignarEquipoDialog
        item={asignarItem}
        open={asignarOpen}
        onOpenChange={setAsignarOpen}
      />
    </div>
  );
}
